import * as RadioGroup from "@radix-ui/react-radio-group";
import { cn } from "@/lib/utils";

export interface ChipRadioOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
}

interface ChipRadioGroupProps<T extends string = string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ChipRadioOption<T>[];
  size?: "sm" | "md";
  invalid?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function ChipRadioGroup<T extends string = string>({
  value,
  onValueChange,
  options,
  size = "md",
  invalid,
  className,
  ariaLabel,
}: ChipRadioGroupProps<T>) {
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      aria-label={ariaLabel}
      className={cn("flex flex-wrap justify-center gap-2", className)}
    >
      {options.map((opt) => (
        <RadioGroup.Item
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          // min-w shrinks on mobile so three chips can flex-wrap onto
          // two rows on a 390 px viewport instead of insisting on a
          // single row that overflows the wizard card.
          //
          // min-h keeps every chip at a ≥44px touch target — these are
          // the primary answer mechanism on a tablet-first form, and
          // text-size padding alone left them at ~24–32px.
          className={cn(
            "inline-flex items-center justify-center rounded-full border bg-foreground/[0.03] font-semibold text-foreground/90 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            size === "sm"
              ? "min-h-[44px] min-w-[6rem] px-3 py-1 text-xs sm:min-w-[7rem]"
              : "min-h-[44px] min-w-[7rem] px-4 py-1.5 text-sm sm:min-w-[9rem]",
            "border-border data-[state=checked]:border-primary data-[state=checked]:bg-gradient-to-br data-[state=checked]:from-primary/25 data-[state=checked]:to-[hsl(var(--accent-2)/0.18)] data-[state=checked]:text-foreground data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40",
            invalid && "border-destructive shadow-[0_0_0_3px_hsl(var(--destructive)/0.18)]",
          )}
        >
          {opt.label}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
